/* wls-byob.js */

class WlsByob extends HTMLElement {
  constructor() {
    super();

    // State
    this.selectedFilters = new Set(['all']);
    this.items = []; // slot items: { key, title, img, category, priceText, priceOldText, priceNewText, lists }
    this.currentProductEl = null;

    // Selling plan state (matched by full plan name)
    this.selectedSellingPlanName = null;

    // Bindings
    this._onClick = this._onClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onChange = this._onChange.bind(this);
  }

  connectedCallback() {
    // Refs (scoped)
    this.$pills = this.querySelector('.js-wls-byob-pills');
    this.$productsWrap = this.querySelector('.wls-byob__products');

    this.$progress = this.querySelector('.wls-byob__progress');
    this.$toggle = this.querySelector('.wls-byob__progress-mobile-toggle');
    this.$closeMobModal = this.querySelector('.wls-byob__progress-mobile-close');

    this.$slotsWrap = this.querySelector('.wls-byob__progress-slots');
    this.$steps = Array.from(this.querySelectorAll('.wls-byob__progress-step'));
    this.$msg = this.querySelector('.wls-byob__progress-message');
    this.$cta = this.querySelector('.wls-byob__progress-cta');
    this.$selectedCount = this.querySelector('.wls-byob__progress-selected');

    this.$pricesWrap = this.querySelector('.wls-byob__progress-prices');
    this.$priceOld = this.querySelector('.wls-byob__progress-price-old');
    this.$priceNew = this.querySelector('.wls-byob__progress-price-new');

    // Mobile UI
    this.$mobileBuyBtn = this.querySelector('.wls-byob__progress-mobile-price-btn');
    this.$mobileToggle = this.querySelector('.wls-byob__progress-mobile-toggle');
    this.$mobilePrice = this.querySelector('.wls-byob__progress-mobile-price');
    this.$mobileBuyBtnPrice = this.$mobileBuyBtn?.querySelector('span') || null;

    // Purchase options UI (inside your snippet)
    this.$purchaseWrap = this.querySelector('.wls-buy-box__purchase');
    this.$purchaseSubInput = this.querySelector('#purchase-subscribe');
    this.$purchaseOneInput = this.querySelector('#purchase-onetime');
    this.$frequencySelect = this.querySelector('#frequency-byob');

    this.$popup = this.querySelector('.wls-byob__product-popup');

    // Listeners
    this.addEventListener('click', this._onClick);
    this.addEventListener('change', this._onChange);
    document.addEventListener('keydown', this._onKeydown);

    // Pills drag scroll
    this._initPillsDragScroll();

    // Body class (matches old script)
    document.body.classList.add('is-byob');

    // Initial selling plan state
    this.selectedSellingPlanName = this._readSelectedSellingPlanName();

    // NiceSelect init (moved from DOMContentLoaded)
    this._initFrequencyNiceSelect();

    // Initial
    this._applyFilters();
    this._render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('change', this._onChange);
    document.removeEventListener('keydown', this._onKeydown);
  }

  /* =========================
     EVENT HANDLERS
  ========================= */

  _onClick(e) {
    // Close mobile modal button (stop bubbling)
    if (e.target.closest('.wls-byob__progress-mobile-close')) {
      e.stopPropagation();
      this.$progress?.classList.remove('is-open');
      document.body.classList.remove('prevent-scroller');
      return;
    }

    // Toggle mobile progress open
    if (e.target.closest('.wls-byob__progress-mobile-toggle')) {
      this.$progress?.classList.toggle('is-open');
      document.body.classList.toggle('prevent-scroller');
      return;
    }

    // PURCHASE OPTIONS (subscribe / one time)
    const purchaseOptionEl = e.target.closest('.wls-buy-box__option');
    if (purchaseOptionEl && this.contains(purchaseOptionEl)) {
      const isOne = purchaseOptionEl.classList.contains('wls-buy-box__option--one');
      const isSub = purchaseOptionEl.classList.contains('wls-buy-box__option--sub');

      if (isOne && this.$purchaseOneInput) {
        this.$purchaseOneInput.checked = true;
      } else if (isSub && this.$purchaseSubInput) {
        this.$purchaseSubInput.checked = true;
      }

      // Sync active classes
      const subOpt = this.querySelector('.wls-buy-box__option--sub');
      const oneOpt = this.querySelector('.wls-buy-box__option--one');
      if (subOpt) subOpt.classList.toggle('is-active', !!this.$purchaseSubInput?.checked);
      if (oneOpt) oneOpt.classList.toggle('is-active', !!this.$purchaseOneInput?.checked);

      // Disable frequency when one-time
      if (this.$frequencySelect) this.$frequencySelect.disabled = !!this.$purchaseOneInput?.checked;

      // Update state + UI
      this.selectedSellingPlanName = this._readSelectedSellingPlanName();
      this._updateCheckoutLinks();
      this._updateProgressPrices();

      return;
    }

    // Close popup (X)
    if (e.target.closest('.wls-byob__product-popup-close')) {
      this._closePopup();
      return;
    }

    // Click on overlay to close popup
    if (this.$popup && e.target === this.$popup) {
      this._closePopup();
      return;
    }

    // Pill click
    const pill = e.target.closest('.wls-byob__pill');
    if (pill) {
      this._handlePillClick(pill);
      return;
    }

    // Product info trigger => open popup
    const trigger = e.target.closest('.wls-byob__product-info-trigger');
    if (trigger) {
      const productEl = trigger.closest('.wls-byob__product');
      if (!productEl) return;
      this._openPopupFromProduct(productEl);
      return;
    }

    // Slot remove by X (removes that exact slot index)
    const removeBtn = e.target.closest('.wls-byob__progress-product-remove');
    if (removeBtn) {
      const slot = removeBtn.closest('.wls-byob__progress-slot.is-filled');
      if (!slot) return;
      const idx = Number(slot.dataset.index);
      if (!Number.isNaN(idx)) this._removeItemAt(idx);
      return;
    }

    // ADD
    const addBtn = e.target.closest('.js-wls-byob-add');
    if (addBtn) {
      e.preventDefault();
      const isPopup = addBtn.classList.contains('js-wls-byob-popup-add');
      const productEl = isPopup ? this.currentProductEl : addBtn.closest('.wls-byob__product');
      if (!productEl) return;

      this._addOne(productEl);
      return;
    }

    // PLUS
    const plusBtn = e.target.closest('.js-wls-byob-plus');
    if (plusBtn) {
      e.preventDefault();
      const isPopup = plusBtn.classList.contains('js-wls-byob-popup-plus');
      const productEl = isPopup ? this.currentProductEl : plusBtn.closest('.wls-byob__product');
      if (!productEl) return;

      this._addOne(productEl);
      return;
    }

    // MINUS
    const minusBtn = e.target.closest('.js-wls-byob-minus');
    if (minusBtn) {
      e.preventDefault();
      const isPopup = minusBtn.classList.contains('js-wls-byob-popup-minus');
      const productEl = isPopup ? this.currentProductEl : minusBtn.closest('.wls-byob__product');
      if (!productEl) return;

      this._removeOneByProduct(productEl);
      return;
    }
  }

  _onChange() {
    // Purchase options changes or frequency changes
    this.selectedSellingPlanName = this._readSelectedSellingPlanName();
    this._syncPurchaseOptionsUI();

    // Keep URL and prices in sync
    this._updateCheckoutLinks();
    this._updateProgressPrices();
  }

  _onKeydown(e) {
    if (e.key === 'Escape') this._closePopup();
  }

  /* =========================
     PILLS / FILTERS
  ========================= */

  _handlePillClick(pill) {
    const filter = pill.getAttribute('data-filter');
    if (!filter) return;

    if (filter === 'all') {
      this.selectedFilters.clear();
      this.selectedFilters.add('all');
    } else {
      if (this.selectedFilters.has(filter)) this.selectedFilters.delete(filter);
      else this.selectedFilters.add(filter);

      // Remove "all" if any category active
      if (this.selectedFilters.size > 0) this.selectedFilters.delete('all');

      // Fallback to all
      if (this.selectedFilters.size === 0) this.selectedFilters.add('all');
    }

    // Update pill classes
    this.querySelectorAll('.wls-byob__pill').forEach(p => {
      const f = p.getAttribute('data-filter');
      p.classList.toggle('is-active', this.selectedFilters.has(f));
    });

    this._applyFilters();
  }

  _applyFilters() {
    const cards = this.querySelectorAll('.wls-byob__product');
    const showAll = this.selectedFilters.has('all');

    cards.forEach(card => {
      const cat = card.getAttribute('data-category') || '';
      const visible = showAll ? true : this.selectedFilters.has(cat);
      card.style.display = visible ? '' : 'none';
    });
  }

  _initPillsDragScroll() {
    const pillsContainer = this.$pills;
    if (!pillsContainer) return;

    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;

    pillsContainer.addEventListener('mousedown', e => {
      isDown = true;
      pillsContainer.classList.add('active');
      startX = e.pageX - pillsContainer.offsetLeft;
      scrollLeft = pillsContainer.scrollLeft;
    });

    pillsContainer.addEventListener('mouseleave', () => {
      isDown = false;
      pillsContainer.classList.remove('active');
    });

    pillsContainer.addEventListener('mouseup', () => {
      isDown = false;
      pillsContainer.classList.remove('active');
    });

    pillsContainer.addEventListener('mousemove', e => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - pillsContainer.offsetLeft;
      const walk = (x - startX) * 2;
      pillsContainer.scrollLeft = scrollLeft - walk;
    });

    let touchStartX = 0;
    let touchScrollLeft = 0;

    pillsContainer.addEventListener('touchstart', e => {
      touchStartX = e.touches[0].pageX;
      touchScrollLeft = pillsContainer.scrollLeft;
    });

    pillsContainer.addEventListener(
      'touchmove',
      e => {
        const x = e.touches[0].pageX;
        const walk = (x - touchStartX) * 2;
        pillsContainer.scrollLeft = touchScrollLeft - walk;
      },
      { passive: true }
    );
    const checkIfAtEnd = () => {
      const isAtEnd =
        pillsContainer.scrollLeft + pillsContainer.clientWidth >=
        pillsContainer.scrollWidth - 1;

      document.querySelector('.wls-byob__pills-wrap').classList.toggle('is-at-end', isAtEnd);
    };
    pillsContainer.addEventListener('scroll', checkIfAtEnd);
  }

  _initFrequencyNiceSelect() {
    const select = this.$frequencySelect;
    if (!select) return;

    if (typeof window.NiceSelect === 'undefined') return;

    const next = select.nextSibling;
    const hasNice = next && next.classList && next.classList.contains('nice-select');

    if (hasNice) {
      try {
        window.NiceSelect.destroy(select);
      } catch (_) {
        // ignore
      }
    }

    select.selectedIndex = 0;

    try {
      window.NiceSelect.bind(select);
    } catch (_) {
      // ignore
    }
  }

  /* =========================
     POPUP
  ========================= */

  _openPopupFromProduct(productEl) {
    this.currentProductEl = productEl;

    const popupTitle = this.$popup.querySelector('.wls-byob__product-popup-title');
    const popupImg = this.$popup.querySelector('.wls-byob__product-popup-media img');

    const titleEl = productEl.querySelector('.wls-byob__product-title');
    const imgEl = productEl.querySelector('.wls-byob__product-img');

    const title = titleEl?.textContent?.trim() || '';
    const imgSrc = imgEl?.getAttribute('src') || '';

    popupTitle.textContent = title;
    popupImg.src = imgSrc;

    // NEW: sync popup add button price from the card
    const popupAddBtn = this.$popup.querySelector('.js-wls-byob-popup-add');
    const cardAddBtn = productEl.querySelector('.js-wls-byob-add');
    if (popupAddBtn && cardAddBtn) {
      popupAddBtn.textContent = cardAddBtn.textContent.trim();
      // optional: keep "originalText" consistent for qty toggle logic
      popupAddBtn.dataset.originalText = popupAddBtn.textContent.trim();
    }

    // sync popup controls with current count
    this._syncSingleProductUI(productEl);
    this._applyPopupContent(productEl);

    this.$popup.classList.add('is-active');
    this.$popup.setAttribute('aria-hidden', 'false');
    document.body.classList.add('prevent-scroller');
  }

  _getPopupContentFromProduct(productEl) {
    const script = productEl.querySelector('.js-byob-popup-content[type="application/json"]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (err) {
      console.warn('Invalid popup JSON', err);
      return null;
    }
  }

  _applyPopupContent(productEl) {
    const data = this._getPopupContentFromProduct(productEl);
    if (!data) return;

    const titleEl = this.$popup?.querySelector('.wls-byob__product-popup-title');
    const textWrap = this.$popup?.querySelector('.wls-byob__product-popup-text-wrap');

    if (titleEl && data.title) titleEl.textContent = data.title;

    // description is HTML string -> render it
    if (textWrap) textWrap.innerHTML = data.description || '';
  }

  _closePopup() {
    if (!this.$popup) return;
    this.$popup.classList.remove('is-active');
    this.$popup.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('prevent-scroller');
  }

  /* =========================
     PURCHASE OPTIONS
  ========================= */

  _readPurchaseMode() {
    // Prefer explicit radios
    if (this.$purchaseOneInput?.checked) return 'onetime';
    if (this.$purchaseSubInput?.checked) return 'subscribe';

    // Fallback: active class on wrapper options
    const active = this.querySelector('.wls-buy-box__option.is-active');
    if (active?.classList.contains('wls-buy-box__option--one')) return 'onetime';
    if (active?.classList.contains('wls-buy-box__option--sub')) return 'subscribe';

    // Default
    return 'subscribe';
  }

  _readSelectedSellingPlanName() {
    const mode = this._readPurchaseMode();
    if (mode !== 'subscribe') return null;

    // Your layout: <select id="frequency"><option ...>Selling Plan Name</option></select>
    const select = this.$frequencySelect;
    if (select && select.selectedOptions && select.selectedOptions.length) {
      const opt = select.selectedOptions[0];
      const name = opt.textContent?.trim();
      return name || null;
    }

    return null;
  }

  _readSelectedSellingPlanExtraDiscountPct() {
    const mode = this._readPurchaseMode();
    if (mode !== 'subscribe') return 0;

    const select = this.$frequencySelect;
    if (!select || !select.selectedOptions || !select.selectedOptions.length) return 0;

    const opt = select.selectedOptions[0];
    const raw = opt.getAttribute('data-discount');
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }

  _syncPurchaseOptionsUI() {
    // Keep the `.is-active` classes in sync if user clicks input directly.
    if (!this.$purchaseWrap) return;

    const mode = this._readPurchaseMode();
    const subOpt = this.$purchaseWrap.querySelector('.wls-buy-box__option--sub');
    const oneOpt = this.$purchaseWrap.querySelector('.wls-buy-box__option--one');

    if (subOpt) subOpt.classList.toggle('is-active', mode === 'subscribe');
    if (oneOpt) oneOpt.classList.toggle('is-active', mode === 'onetime');

    // Enable/disable frequency select
    if (this.$frequencySelect) {
      this.$frequencySelect.disabled = mode !== 'subscribe';
    }
  }

  /* =========================
     QUEUE (SLOTS) API
  ========================= */

  _productKeyFromEl(productEl) {
    // Strongest identifier: variant id
    const v = productEl?.dataset?.variantId || productEl?.getAttribute?.('data-variant-id');
    if (v) return `variant:${v}`;

    // Fallback: title
    const title = productEl.querySelector('.wls-byob__product-title')?.textContent?.trim() || '';
    return `title:${title}`;
  }

  _extractSlotItemFromProduct(productEl) {
    const title = productEl.querySelector('.wls-byob__product-title')?.textContent?.trim() || '';
    const img = productEl.querySelector('.wls-byob__product-img')?.getAttribute('src') || '';
    const category = productEl.getAttribute('data-category') || '';

    const priceText = productEl.querySelector('.js-wls-byob-add')?.textContent?.trim() || '';

    // Optional per-product old/new price support (if present in product card markup)
    const priceOldText =
      productEl.querySelector('.wls-byob__product-price-old')?.textContent?.trim() ||
      productEl.querySelector('[data-price-old]')?.getAttribute('data-price-old') ||
      '';

    const priceNewText =
      productEl.querySelector('.wls-byob__product-price-new')?.textContent?.trim() ||
      productEl.querySelector('[data-price-new]')?.getAttribute('data-price-new') ||
      '';

    const lists = Array.from(productEl.querySelectorAll('.wls-byob__product-list'))
      .map(el => el.textContent.trim())
      .filter(Boolean);

    return { title, img, category, priceText, priceOldText, priceNewText, lists };
  }

  _countByKey() {
    const map = new Map();
    for (const it of this.items) map.set(it.key, (map.get(it.key) || 0) + 1);
    return map;
  }

  _addOne(productEl) {
    if (this.items.length >= 6) return;

    const key = this._productKeyFromEl(productEl);
    const base = this._extractSlotItemFromProduct(productEl);

    this.items.push({ key, ...base });

    this._render();
  }

  _removeOneByProduct(productEl) {
    const key = this._productKeyFromEl(productEl);

    // Remove last occurrence of that key
    for (let i = this.items.length - 1; i >= 0; i--) {
      if (this.items[i].key === key) {
        this.items.splice(i, 1);
        this._render();
        return;
      }
    }
  }

  _removeItemAt(index) {
    if (index < 0 || index >= this.items.length) return;
    this.items.splice(index, 1);
    this._render();
  }

  /* =========================
     SELLING PLANS
     Reads JSON from: <script class="js-selling-plans" type="application/json">[...]</script>
  ========================= */

  _getSellingPlansFromProduct(productEl) {
    if (!productEl) return [];
    const script = productEl.querySelector('.js-selling-plans[type="application/json"]');
    if (!script) return [];
    try {
      const json = JSON.parse(script.textContent.trim());
      return Array.isArray(json) ? json : [];
    } catch {
      return [];
    }
  }

  _findProductElByVariantId(variantId) {
    const id = String(variantId);

    // Try the most common attribute name first
    let el = this.querySelector(`.wls-byob__product[data-variant-id="${CSS.escape(id)}"]`);
    if (el) return el;

    // Fallbacks
    el = this.querySelector(`.wls-byob__product[data-variantid="${CSS.escape(id)}"]`);
    if (el) return el;

    el = this.querySelector(`.wls-byob__product[data-variant_id="${CSS.escape(id)}"]`);
    if (el) return el;

    return null;
  }

  /* =========================
     CHECKOUT LINK BUILDER
     /cart/add?items[][id]=...&items[][quantity]=...&items[][selling_plan]=...&return_to=/checkout
  ========================= */

  _getTierDiscountCode() {
    const tier = this._progressTier(); // 0 / 10 / 15
    if (tier === 15) return 'byob_15';
    if (tier === 10) return 'byob_10';
    return null;
  }

  _getByobAttributes() {
    // minimal signal da je ovo BYOB flow
    const tier = this._progressTier();
    return {
      byob: '1',
      byob_tier: String(tier), // "0" | "10" | "15"
    };
  }

  _buildCheckoutAddUrl() {
    const counts = this._countByKey();

    const entries = Array.from(counts.entries())
      .map(([key, quantity]) => {
        const m = String(key).match(/^variant:(\d+)$/);
        if (!m) return null;
        return { id: String(m[1]), quantity: Number(quantity) };
      })
      .filter(Boolean);

    if (!entries.length) return '/checkout';

    const mode = this._readPurchaseMode(); // must return 'subscribe' | 'onetime'
    const selectedName = this._readSelectedSellingPlanName(); // exact plan.name (text)
    this.selectedSellingPlanName = selectedName;

    const params = new URLSearchParams();

    for (const it of entries) {
      params.append('items[][id]', String(it.id));
      params.append('items[][quantity]', String(it.quantity));

      // Only include selling_plan when subscribe is selected
      if (mode === 'subscribe' && selectedName) {
        const productEl = this._findProductElByVariantId(it.id);
        const plans = this._getSellingPlansFromProduct(productEl);

        const match = Array.isArray(plans)
          ? plans.find(sp => sp && String(sp.name || '').trim() === String(selectedName).trim())
          : null;

        if (match?.id) {
          params.append('items[][selling_plan]', String(match.id));
        }
      }
    }

    // add BYOB cart attributes (works on /cart/add)
    const attrs = this._getByobAttributes();
    Object.keys(attrs || {}).forEach(k => {
      params.append(`attributes[${k}]`, String(attrs[k]));
    });

    // add discount code based on tier (only when 10/15)
    const code = this._getTierDiscountCode();

    if (code) {
      params.append('return_to', `/checkout?discount=${code}`);

    } else {
      params.append('return_to', '/checkout');
    }

    return `/cart/add?${params.toString()}`;
  }

  _updateCheckoutLinks() {
    const url = this._buildCheckoutAddUrl();

    if (this.$cta) this.$cta.setAttribute('href', url);
    if (this.$mobileBuyBtn) this.$mobileBuyBtn.setAttribute('href', url);
  }

  /* =========================
     PRICES
  ========================= */

  _updateProgressPrices() {
    if (!this.$priceOld || !this.$priceNew) return;

    const { total, discountedTotal, hasAnyPrice } = this._calculateTotals();

    // If we can't parse any prices, hide the price box + mobile price UI
    if (!hasAnyPrice) {
      if (this.$pricesWrap) this.$pricesWrap.style.display = 'none';

      if (this.$mobilePrice) this.$mobilePrice.textContent = '';
      if (this.$mobileBuyBtnPrice) this.$mobileBuyBtnPrice.textContent = '';

      if (this.$mobileToggle) {
        this.$mobileToggle.classList.remove('wls-byob__progress-mobile-toggle--btn');
      }
      if (this.$mobileBuyBtn) {
        this.$mobileBuyBtn.style.display = 'none';
      }
      return;
    }

    if (this.$pricesWrap) this.$pricesWrap.style.display = '';

    const tier = this._progressTier();
    const hasDiscount = tier > 0 || this._readPurchaseMode() === 'subscribe';

    this.$priceOld.style.display = hasDiscount ? '' : 'none';
    this.$priceOld.textContent = this._formatMoney(total);
    this.$priceNew.textContent = this._formatMoney(discountedTotal);

    if (this.$mobilePrice) this.$mobilePrice.textContent = this._formatMoney(discountedTotal);
    if (this.$mobileBuyBtnPrice) this.$mobileBuyBtnPrice.textContent = this._formatMoney(discountedTotal);

    if (this.$mobileToggle) {
      this.$mobileToggle.classList.toggle('wls-byob__progress-mobile-toggle--btn', this.items.length > 0);
    }
    if (this.$mobileBuyBtn) {
      this.$mobileBuyBtn.style.display = this.items.length > 0 ? '' : 'none';
    }
  }

  _calculateTotals() {
    let total = 0;
    let hasAnyPrice = false;

    for (const item of this.items) {
      const baseText = item.priceNewText || item.priceText;
      const p = this._extractNumberFromPriceText(baseText);
      if (Number.isFinite(p)) {
        total += p;
        hasAnyPrice = true;
      }
    }

    // Tier discount (bundle)
    const tier = this._progressTier(); // 0 / 10 / 15
    const tierFactor = tier === 10 ? 0.9 : tier === 15 ? 0.85 : 1;

    // Extra discount from selling plan (subscribe only)
    const mode = this._readPurchaseMode();
    const extraPct = mode === 'subscribe' ? this._readSelectedSellingPlanExtraDiscountPct() : 0;
    const extraFactor = extraPct > 0 ? 1 - extraPct / 100 : 1;

    const discountedTotal = total * tierFactor * extraFactor;

    return { total, discountedTotal, hasAnyPrice };
  }

  _extractNumberFromPriceText(text) {
    if (!text) return NaN;

    const s = String(text);

    // Grab all number-like chunks
    const matches = s.match(/(\d+[.,]?\d*)/g);
    if (!matches || !matches.length) return NaN;

    const raw = matches[matches.length - 1];

    // Normalize "25,00" -> "25.00"
    const normalized = raw.replace(',', '.');

    const n = Number(normalized);
    return Number.isFinite(n) ? n : NaN;
  }

  _formatMoney(n) {
    const num = Number(n);
    if (!Number.isFinite(num)) return '$0.00';
    return `$${num.toFixed(2)}`;
  }

  /* =========================
     PROGRESS / RENDER
  ========================= */

  _progressTier() {
    const n = this.items.length;
    if (n >= 6) return 15;
    if (n >= 3) return 10;
    return 0;
  }

  _render() {
    // Counter
    if (this.$selectedCount) this.$selectedCount.textContent = String(this.items.length);

    // Steps
    const tier = this._progressTier();
    this.$steps?.forEach((stepEl, i) => {
      const val = i === 0 ? 0 : i === 1 ? 10 : 15;
      stepEl.classList.toggle('is-active', val <= tier);
    });

    // Message
    if (this.$msg) {
      const n = this.items.length;

      // Tier thresholds
      const firstTierAt = 3; // -> 10%
      const secondTierAt = 6; // -> 15%

      if (n >= secondTierAt) {
        this.$msg.textContent = 'You unlocked 15% off';
      } else if (n >= firstTierAt) {
        const left = secondTierAt - n; // 1..3
        this.$msg.textContent = `Add ${left} more product${left === 1 ? '' : 's'} to get 15% off`;
      } else {
        const left = firstTierAt - n; // 1..3
        this.$msg.textContent = `Add ${left} more product${left === 1 ? '' : 's'} to get 10% off`;
      }
    }


    // CTA
    if (this.$cta) this.$cta.style.display = this.items.length > 0 ? '' : 'none';

    // Hide subscriptions until at least 1 product is added
    if (this.$purchaseWrap) {
      const hasItems = this.items.length > 0;
      this.$purchaseWrap.style.display = hasItems ? '' : 'none';
    }


    // Slots
    this._renderSlots();

    // Card + popup qty UI sync
    this._syncAllProductsUI();

    // Equal heights for slot titles
    this._setEqualSlotTitleHeight();

    // Purchase options UI sync
    this._syncPurchaseOptionsUI();

    // Keep checkout link in sync with queue + purchase option
    this._updateCheckoutLinks();

    // Prices (desktop + mobile)
    this._updateProgressPrices();
  }

  _renderSlots() {
    if (!this.$slotsWrap) return;

    this.$slotsWrap.innerHTML = '';

    for (let i = 0; i < 6; i++) {
      const item = this.items[i];

      if (!item) {
        const empty = document.createElement('div');
        empty.className = 'wls-byob__progress-slot';
        empty.innerHTML = '<span>+</span>';
        this.$slotsWrap.appendChild(empty);
        continue;
      }

      const slot = document.createElement('div');
      slot.className = 'wls-byob__progress-slot is-filled';
      slot.dataset.index = String(i);

      const listsHtml = item.lists?.length
        ? `<div class="wls-byob__progress-product-lists">
            ${item.lists.map(t => `<span class="wls-byob__progress-product-list">${this._escapeHtml(t)}</span>`).join('')}
           </div>`
        : '';

      let slotOld = item.priceOldText || '';
      let slotNew = item.priceNewText || '';

      if (!slotNew) {
        slotNew = item.priceText ? item.priceText.replace(/^ADD\s*–\s*/i, '') : '';
      }

      const priceHtml = slotNew
        ? `<div class="wls-byob__progress-product-prices">
            ${slotOld ? `<span class="wls-byob__progress-product-price-old">${this._escapeHtml(slotOld)}</span>` : ''}
            <span class="wls-byob__progress-product-price-new">${this._escapeHtml(slotNew)}</span>
           </div>`
        : '';

      slot.innerHTML = `
        <div class="wls-byob__progress-product">
          <div class="wls-byob__progress-product-info">
            <div class="wls-byob__progress-product-info-image">
              <img src="${this._escapeAttr(item.img)}" width="100" height="100" alt="${this._escapeAttr(item.title)}">
            </div>
            <h4 class="wls-byob__progress-product-info-title" title="${this._escapeHtml(item.title)}">${this._escapeHtml(
        item.title
      )}</h4>
            ${listsHtml}
          </div>
          ${priceHtml}
        </div>
        <span class="wls-byob__progress-product-remove">×</span>
      `;

      this.$slotsWrap.appendChild(slot);
    }
  }

  _syncAllProductsUI() {
    const counts = this._countByKey();

    this.querySelectorAll('.wls-byob__product').forEach(productEl => {
      const key = this._productKeyFromEl(productEl);
      const count = counts.get(key) || 0;
      this._applyCountUIToProduct(productEl, count);
    });

    // Also sync popup controls if open
    if (this.$popup?.classList.contains('is-active') && this.currentProductEl) {
      const key = this._productKeyFromEl(this.currentProductEl);
      const count = counts.get(key) || 0;
      this._applyCountUIToPopup(count);
    }
  }

  _syncSingleProductUI(productEl) {
    const counts = this._countByKey();
    const key = this._productKeyFromEl(productEl);
    const count = counts.get(key) || 0;

    this._applyCountUIToProduct(productEl, count);
    this._applyCountUIToPopup(count);
  }

  _applyCountUIToProduct(productEl, count) {
    const addBtn = productEl.querySelector('.js-wls-byob-add');
    const qtyBox = productEl.querySelector('.js-wls-byob-qty');
    const qtyNum = qtyBox?.querySelector('.wls-byob__qty-value-num');

    if (!addBtn || !qtyBox || !qtyNum) return;

    // Cache original add text once (so we can restore it)
    if (!addBtn.dataset.originalText) addBtn.dataset.originalText = addBtn.textContent.trim();

    if (count <= 0) {
      // Show add, hide qty
      qtyBox.classList.remove('is-active');
      qtyBox.setAttribute('aria-hidden', 'true');
      addBtn.classList.remove('not-visible');
      addBtn.textContent = addBtn.dataset.originalText;
      qtyNum.textContent = '1';
      return;
    }

    // Hide add, show qty
    addBtn.classList.add('not-visible');
    qtyBox.classList.add('is-active');
    qtyBox.setAttribute('aria-hidden', 'false');

    // Show real count and update label to "in cart"
    qtyNum.textContent = String(count);
    const label = qtyBox.querySelector('.wls-byob__qty-value');
    if (label) {
      const numSpan = label.querySelector('.wls-byob__qty-value-num');
      label.innerHTML = `${
        numSpan
          ? numSpan.outerHTML
          : `<span class="wls-byob__qty-value-num">${this._escapeHtml(String(count))}</span>`
      } in cart`;
    }
  }

  _applyCountUIToPopup(count) {
    if (!this.$popup) return;

    const popupAddBtn = this.$popup.querySelector('.js-wls-byob-popup-add');
    const popupQtyBox = this.$popup.querySelector('.js-wls-byob-popup-qty');
    const popupQtyNum = popupQtyBox?.querySelector('.wls-byob__qty-value-num');

    if (!popupAddBtn || !popupQtyBox || !popupQtyNum) return;

    if (!popupAddBtn.dataset.originalText) popupAddBtn.dataset.originalText = popupAddBtn.textContent.trim();

    if (count <= 0) {
      popupQtyBox.classList.remove('is-active');
      popupQtyBox.setAttribute('aria-hidden', 'true');
      popupAddBtn.classList.remove('not-visible');
      popupAddBtn.textContent = popupAddBtn.dataset.originalText;
      popupQtyNum.textContent = '1';
      return;
    }

    popupAddBtn.classList.add('not-visible');
    popupQtyBox.classList.add('is-active');
    popupQtyBox.setAttribute('aria-hidden', 'false');

    popupQtyNum.textContent = String(count);
    const label = popupQtyBox.querySelector('.wls-byob__qty-value');
    if (label) {
      const numSpan = label.querySelector('.wls-byob__qty-value-num');
      label.innerHTML = `${
        numSpan
          ? numSpan.outerHTML
          : `<span class="wls-byob__qty-value-num">${this._escapeHtml(String(count))}</span>`
      } in cart`;
    }
  }

  _setEqualSlotTitleHeight() {
    const titles = this.querySelectorAll('.wls-byob__progress-product-info-title');
    if (!titles.length) return;

    titles.forEach(t => (t.style.height = 'auto'));

    let max = 0;
    titles.forEach(t => {
      const h = t.offsetHeight;
      if (h > max) max = h;
    });

    titles.forEach(t => (t.style.height = `${max}px`));
  }

  /* =========================
     ESCAPING
  ========================= */

  _escapeHtml(str) {
    return String(str)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  _escapeAttr(str) {
    return this._escapeHtml(str);
  }
}

if (!customElements.get('wls-byob')) {
  customElements.define('wls-byob', WlsByob);
}
